import React, { Component, Fragment, startTransition } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container, Row, Col } from 'reactstrap';
import { get, isArray, isEmpty, isNull } from 'lodash-es';
import classnames from 'classnames';

import {
  DealerIcoWidgetModel,
  buildDealerIcoWidgetConfigurationPath,
  DealerIcoWidgetEntities,
} from 'client/data/models/dealer-ico-widget';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { DigitalCheckoutModel, PATHS as DigitalCheckoutPaths } from 'client/data/models/digital-checkout';
import { PrivateListingModel, PATHS as PrivateListingPaths } from 'client/data/models/private-listing';
import { RequestModel } from 'client/data/models/request';
import { FeatureSpecsPaths, VehicleModel, VehicleEntities } from 'client/data/models/vehicle-v2';
import { VinOverview } from 'site-modules/inventory/components/vin-overview/vin-overview';
import { VdpNavigation } from 'site-modules/inventory/components/vdp-navigation/vdp-navigation';
import { PriceSummary } from 'site-modules/inventory/components/price-summary/price-summary';
import { Negotiation } from 'site-modules/inventory/components/negotiation/negotiation';
import { VdpGroup } from 'site-modules/inventory/components/vdp-group/vdp-group';
import { FeaturesAndSpecs } from 'site-modules/inventory/components/features-and-specs/features-and-specs';
import { FeaturesAndSpecsPrivate } from 'client/site-modules/inventory/components/features-and-specs-private/features-and-specs-private';
import { VehicleSummary } from 'site-modules/shared/components/inventory/vehicle-summary/vehicle-summary';
import { InventoryComments } from 'client/site-modules/shared/components/inventory/inventory-comments/inventory-comments';
import { DealerInfo } from 'site-modules/inventory/components/dealer-info/dealer-info';
import { DealerReviews } from 'site-modules/shared/components/dealer-reviews/dealer-reviews';
import { VdpPaymentCalculatorAsync } from 'site-modules/shared/components/inventory/vdp-calculator/vdp-payment-calculator-async';
import { VdpBreadcrumbs } from 'site-modules/inventory/components/vdp-breadcrumbs/vdp-breadcrumbs';
import { InventoryHero } from 'site-modules/inventory/components/inventory-hero/inventory-hero';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { VdpSimilarVehicles } from 'client/site-modules/shared/components/vdp-similar-vehicles/vdp-similar-vehicles';
import { VdpDisclaimer } from 'site-modules/shared/components/inventory/vdp-disclaimer/vdp-disclaimer';
import { VdpAdUnit } from 'site-modules/inventory/components/vdp-ad-unit/vdp-ad-unit';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';

import { isRenderedAdUnit } from 'client/utils/ads';
import { hasDealerAddressInfo } from 'site-modules/shared/utils/inventory-utils/has-dealer-address-info';
import { hasSellerAddressInfo } from 'site-modules/shared/utils/inventory-utils/has-seller-address-info';
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getDigitalRetailAvailablePrograms } from 'site-modules/shared/utils/inventory-utils/get-digital-retail-available-programs';
import { GRID } from 'site-modules/inventory/constants/grid';
import { NAVIGATION_IDS } from 'site-modules/shared/constants/inventory/vdp-navigation';
import { ConsumerReviewVdp } from 'site-modules/shared/components/inventory/consumer-review-vdp/consumer-review-vdp';
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
import {
  PAYMENT_TYPE_STORAGE_KEY,
  CALCULATOR_TYPE_LEASE,
  CALCULATOR_TYPE_LOAN,
} from 'site-modules/shared/constants/calculator/calculator';
import { Storage } from 'client/site-modules/shared/utils/storage';
import {
  defineOpenedCalculator,
  getDefaultLeaseDownPayment,
} from 'site-modules/shared/utils/calculator/calculator-helper';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { DealerInfoEntities, DealerInfoModel, getDealerInfoPath } from 'client/data/models/dealer-info';
import { isCarMax, isPartnerDealer, isDriveway, isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import {
  isUsedDealCheckAvailable,
  isUsedNonPartnerDealerDealCheckAvailable,
} from 'site-modules/shared/utils/car-buying/is-used-deal-check-available';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { VdpPriceChecker } from 'client/site-modules/inventory/components/vdp-price-checker/vdp-price-checker';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { getPriceChange } from 'site-modules/shared/utils/inventory/price-change';
import { HISTORY_PRICES_CONCEALED_STATE } from 'client/site-modules/shared/constants/inventory/prices';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { getLeaseMonthlyPayment } from 'site-modules/inventory/components/price-summary/price-summary-utils';
import { LEASE_PAYMENT, LOAN_PAYMENT } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { isLeasePaymentsEligible } from 'client/site-modules/shared/utils/inventory-utils/is-lease-payments-eligible';
import { RECENTLY_VIEWED_STORAGE } from 'site-modules/shared/components/inventory/constants/recently-viewed-vins';
import { getPhoto360 } from 'site-modules/shared/utils/get-photo-360';
import { MidPageWidgetView } from 'client/site-modules/inventory/components/mid-page-widget-view/mid-page-widget-view';
import { isBatteryRangeModuleEnabled } from 'client/site-modules/shared/utils/inventory/is-recurrent-available';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { InventorySimilarVinsModel } from 'client/data/models/inventory-similar-vins';
import { VehicleExtendedDetails } from 'site-modules/inventory/components/vehicle-extended-details/vehicle-extended-details';
import { getVdpCtas } from 'site-modules/shared/utils/inventory/get-vdp-ctas';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { FREE_CODE, PAID_CODE } from 'site-modules/shared/components/delivery/constants';
import { CapitalOnePersonalizedPayment } from 'site-modules/shared/components/financing/capital-one-personalized-payments/capital-one-personalized-payment';
import { FinancingModel, PATHS } from 'client/data/models/financing';
import { isCapOneProvider, isAvailableLocation, isEligible } from 'site-modules/shared/utils/financing/financing';
import { AppraisalModule } from 'site-modules/shared/components/appraisal-module/appraisal-module';
import { WarrantyModule } from 'site-modules/shared/components/inventory/warranty-module/warranty-module';
import { getDealerRooftopId } from 'client/site-modules/shared/utils/dealer';
import {
  getVinWarrantyPath,
  getPurchasePrice,
  hasDealerFees,
  hasNoDealerFees,
} from 'site-modules/shared/utils/inventory/vdp-utils';
import { isWarrantyApplicable, isWarrantyDefined } from 'site-modules/shared/utils/inventory/warranty';
import { EvInsights } from 'site-modules/shared/components/inventory/ev-insights/ev-insights';
import { isEvModuleApplicable } from 'site-modules/shared/utils/inventory/is-ev-module-applicable';
import { CapitalOneEntryPointMainWell } from 'site-modules/shared/components/financing/capital-one-entry-point/capital-one-entry-point-main-well';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import convexMask from 'site-modules/shared/components/clipped-container/mask-library/convex-header';
import { isPlugInHybridVehicle } from 'site-modules/shared/utils/inventory-utils/engine';
import { PhevInsights } from 'site-modules/shared/components/inventory/ev-insights/phev-insights';
import { IcoWidgetAsync } from 'site-modules/shared/components/dealer-ico-widget/ico-widget/ico-widget-async';
import {
  ICO_ON_EDMUNDS_CREATIVE_IDS,
  ICO_WIDGET_DISPLAY_TYPES,
} from 'site-modules/shared/components/dealer-ico-widget/constants';
import { VdpCertifiedPreOwned } from 'site-modules/shared/components/inventory/vdp-certified-pre-owned/vdp-certified-pre-owned';
import { SellerInfo } from 'client/site-modules/inventory/components/seller-info/seller-info';
import { BOL_VDP_DEALERS } from 'site-modules/shared/constants/inventory/buy-online-dealers';
import { PUB_STATES } from 'client/constants/pub-states';
import { transformInventoryComments } from 'site-modules/shared/utils/inventory/seller-comment-utils';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getTCPAParentDealershipName } from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import { isLeaseEnabled, LEASE_PAYMENT_DATA_PATH } from 'site-modules/shared/utils/inventory-utils/is-lease-enabled';
import { createPrivatePartyDeal } from 'site-modules/shared/utils/financing/caramel-utils';
import { buildFailedToFetchErrorValue, getMakeOfferErrorMessage } from 'site-modules/shared/utils/errors-handlers';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import { STATUS_ERROR, STATUS_SUCCESS } from 'site-modules/shared/constants/private-party/private-party';
import { EngagementHandler } from 'client/engagement-handlers';
import { PrivatePartyEngagementHandler } from 'client/engagement-handlers/private-party-engagement-handler/private-party-engagement-handler';
import { SellerComments } from 'client/site-modules/shared/components/private-party/seller-comments/seller-comments';
import { LISTING_STATUS_APPROVED } from 'site-modules/shared/constants/private-party/car-details';
import { openLinkInNewTab } from 'site-modules/shared/utils/open-link-in-new-tab';
import { ManageListingContainerAsync } from 'site-modules/shared/components/private-party/manage-listing-container/manage-listing-container-async';
import { VehicleCondition } from 'site-modules/inventory/components/vehicle-condition/vehicle-condition';
import { PriceDetails } from 'site-modules/shared/components/inventory/price-details/price-details';
import { DealerFeesPriceDetails } from 'site-modules/shared/components/inventory/dealer-fees-price-details/dealer-fees-price-details';
import { StickyToBottom } from 'site-modules/shared/components/sticky-to-bottom/sticky-to-bottom';

import './vdp.scss';
import './common.scss';

EngagementHandler.register(PrivatePartyEngagementHandler);

export function VdpAppraisalModuleUI({ isMobile = false, icoWidgetConfig = {} }) {
  const isIcoWidgetActive = Boolean(icoWidgetConfig.isActive);

  return (
    !isIcoWidgetActive && (
      <VdpModule
        className="bg-cool-gray-90 mb-1_5"
        headerTag="h2"
        sectionTitle="Trade-In Value"
        titleClassName="heading-3"
      >
        <VdpModule className="bg-white" indentationSize={1.5}>
          <AppraisalModule
            title="Have a vehicle to trade?"
            subCopy="See your car’s value in minutes with our free appraisal tool. Find out how much you can put towards your next car."
            isMobile={isMobile}
            headerTag="h3"
          />
        </VdpModule>
      </VdpModule>
    )
  );
}

VdpAppraisalModuleUI.propTypes = {
  isMobile: PropTypes.bool,
  dealerId: PropTypes.number,
  icoWidgetConfig: DealerIcoWidgetEntities.Configs,
};

const stateToPropsConfig = {
  icoWidgetConfig: bindToPath(
    ({ dealerId }) => buildDealerIcoWidgetConfigurationPath({ rooftopId: dealerId }),
    DealerIcoWidgetModel
  ),
};

const VdpAppraisalModule = connect()(connectToModel(VdpAppraisalModuleUI, stateToPropsConfig));

export class VdpUI extends Component {
  static propTypes = {
    vinData: InventoryEntities.InventoryVin,
    vehicleData: VehicleEntities.MakeModelSubmodelYear,
    isMobile: PropTypes.bool.isRequired,
    forceLease: PropTypes.bool,
    pathName: PropTypes.string,
    zipCode: PropTypes.string.isRequired,
    isNonTMVDealer: PropTypes.bool,
    showPriceAnalysisSection: PropTypes.bool,
    radius: PropTypes.number,
    dealershipInfo: DealerInfoEntities.DealerInfo,
    disableInventoryMLPhotoSort: PropTypes.bool,
    doNotSell: PropTypes.bool,
    stateCode: PropTypes.string,
    isBLT2125DisableCarmax360: PropTypes.bool,
    isBLT2125DisableSpinCar360: PropTypes.bool,
    isSHOP413DisableFlickFusion360: PropTypes.bool,
    inventory: PropTypes.oneOfType([InventoryEntities.Inventories, InventoryEntities.InventoriesVin]),
    pageNameLegacy: PropTypes.string,
    isPrequalified: PropTypes.bool,
    financingOffers: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
    warranty: InventoryEntities.Warranty,
    isAllowBypassApiGateway: PropTypes.bool,
    icoWidgetConfig: DealerIcoWidgetEntities.Configs,
    dealer: PropTypes.shape({}),
    enableCaramel: PropTypes.bool,
    ted6504Recipe: PropTypes.string,
    disableDrivewayNewCarLeadForms: PropTypes.bool,
    location: PropTypes.shape({}),
    privateListingData: PropTypes.shape({}),
    styleFeatures: PropTypes.shape({}),
    visitorId: PropTypes.string,
    sessionId: PropTypes.string,
    protocol: PropTypes.string,
    hostName: PropTypes.string,
    isAuthenticated: PropTypes.bool,
    userId: PropTypes.string,
    isDealerFeesEnabled: PropTypes.bool,
  };

  static defaultProps = {
    vinData: {},
    vehicleData: {},
    forceLease: false,
    pathName: '',
    isNonTMVDealer: false,
    showPriceAnalysisSection: false,
    radius: null,
    dealershipInfo: null,
    disableInventoryMLPhotoSort: false,
    doNotSell: false,
    stateCode: null,
    isBLT2125DisableCarmax360: false,
    isBLT2125DisableSpinCar360: false,
    isSHOP413DisableFlickFusion360: false,
    inventory: [],
    pageNameLegacy: '',
    isPrequalified: false,
    financingOffers: null,
    warranty: [],
    isAllowBypassApiGateway: false,
    icoWidgetConfig: {},
    dealer: null,
    enableCaramel: false,
    ted6504Recipe: 'ctrl',
    disableDrivewayNewCarLeadForms: false,
    location: {},
    privateListingData: null,
    styleFeatures: null,
    visitorId: null,
    sessionId: null,
    protocol: '',
    hostName: '',
    isAuthenticated: null,
    userId: '',
    isDealerFeesEnabled: false,
  };

  constructor(props) {
    super(props);

    if (!this.storage && typeof window !== 'undefined') this.storage = new Storage('localStorage');
    const selectedPaymentType = this.storage && this.storage.get(PAYMENT_TYPE_STORAGE_KEY);
    const forcedCalcType = props.forceLease ? CALCULATOR_TYPE_LEASE : '';

    const { vinData } = this.props;

    this.state = {
      calculatorPayments: {},
      openedCalculatorForm: getPurchasePrice(vinData)
        ? defineOpenedCalculator(forcedCalcType, vinData.type, selectedPaymentType)
        : CALCULATOR_TYPE_LOAN,
      isIncentivesAdRendered: true,
      isDRLeadFormSubmitAvailable: false,
    };
  }

  componentDidMount() {
    this.setRecentlyViewedVins();
    const { vinData, icoWidgetConfig } = this.props;
    const { inTransit } = vinData;
    if (isPartnerDealer(vinData) && inTransit) {
      this.fireInTransit();
    }

    this.fireDeliveryCost();
    this.fireIsIcoWidgetActiveConfig(icoWidgetConfig);
  }

  setRecentlyViewedVins() {
    const { vin } = this.props.vinData;
    let recentlyViewedVins = this.storage.get(RECENTLY_VIEWED_STORAGE.PATH) || [];

    if (!recentlyViewedVins.includes(vin)) {
      if (recentlyViewedVins.length >= RECENTLY_VIEWED_STORAGE.MAX_COUNT) {
        recentlyViewedVins = recentlyViewedVins.slice(-(RECENTLY_VIEWED_STORAGE.MAX_COUNT - 1));
      }
      recentlyViewedVins.push(vin);
      this.storage.set('recentlyViewedVins', recentlyViewedVins);
    }
  }

  /**
   * Gets availability state of digital retail features.
   * @param {boolean} isNewVehicle
   * @returns {object}
   */
  getDigitalRetailAvailability = isNewVehicle => {
    const { isNonTMVDealer, vinData } = this.props;
    const { leaseProgram, loanProgram } = getDigitalRetailAvailablePrograms(vinData);
    const isLeaseAvailable = !!leaseProgram;
    const isLoanAvailable = !!loanProgram;

    if (isNewVehicle) {
      if (isNonTMVDealer) {
        return { isDigitalRetailAvailableForOpenedForm: false, isDigitalRetailAvailable: false };
      }

      const { openedCalculatorForm } = this.state;

      return {
        isDigitalRetailAvailableForOpenedForm:
          openedCalculatorForm === CALCULATOR_TYPE_LEASE ? isLeaseAvailable : isLoanAvailable,
        isDigitalRetailAvailable: isLeaseAvailable || isLoanAvailable,
      };
    }

    return {
      isDigitalRetailAvailableForOpenedForm: isLoanAvailable,
      isDigitalRetailAvailable: isLoanAvailable,
    };
  };

  fireInTransit = () => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'in_transit_vin',
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      },
    });
  };

  fireDeliveryCost = () => {
    const { vinData, pageNameLegacy } = this.props;
    const parentDealershipName = get(vinData, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
    const isCarMaxDealer = isCarMax(parentDealershipName);
    const isDrivewayDealer = isDriveway(parentDealershipName);
    const deliveryOptions = get(vinData, 'computedDisplayInfo.deliveryOptions');
    const deliveryCost = get(deliveryOptions, 'deliveryCost');
    const deliveryCode = get(deliveryOptions, 'deliveryCode');

    if ([FREE_CODE, PAID_CODE].includes(deliveryCode) && (isCarMaxDealer || isDrivewayDealer))
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: TrackingConstant.SUBACTION_SHOW_DELIVERY_COST,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          creative_id: `${isUsed(get(vinData, 'type', '')) ? 'used' : 'new'}-summary_phonenum-${pageNameLegacy}`,
          value: deliveryCode === FREE_CODE ? 0 : deliveryCost,
        },
      });
  };

  fireIsIcoWidgetActiveConfig = icoWidgetConfig =>
    !isEmpty(icoWidgetConfig) &&
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        creative_id: ICO_ON_EDMUNDS_CREATIVE_IDS.VEHICLE_LOOKUP,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: TrackingConstant.SUBACTION_ICO_DEALER,
        value: get(icoWidgetConfig, 'isActive', false),
      },
    });

  handleCalculatorPaymentsUpdate = calculatorPayments =>
    startTransition(() => {
      this.setState({ calculatorPayments });
    });

  hideEmptyAdContainer = slot => {
    this.setState({ isIncentivesAdRendered: isRenderedAdUnit(slot) });
  };

  /**
   * Handles received opened form type.
   * @param {string} openedCalculatorForm
   */
  handleCalculatorToggle = openedCalculatorForm =>
    startTransition(() => {
      this.setState({ openedCalculatorForm });
    });

  /**
   * Handles the type of the submitted lead form. If isDRLeadFormSubmitAvailable is true,
   * then we should submit digital retail lead form. It will be possible if user input custom vehicle price.
   * @param {boolean} isDRLeadFormSubmitAvailable
   */
  handleLeadFormSubmitMethodUpdate = isDRLeadFormSubmitAvailable => this.setState({ isDRLeadFormSubmitAvailable });

  handlePrivatePartyMakeOffer = async () => {
    const { privateListingData, visitorId, sessionId, protocol, hostName } = this.props;

    const deal = await createPrivatePartyDeal({
      listingId: privateListingData?.listingId,
      visitorId,
      sessionId,
      protocol,
      hostName,
    });

    const shareLink = deal?.shareLink;

    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.CREATE_DEAL, {
      status: shareLink ? STATUS_SUCCESS : STATUS_ERROR,
      value: shareLink ? deal?.dealId : buildFailedToFetchErrorValue(deal),
    });

    if (!shareLink) {
      return getMakeOfferErrorMessage(deal?.message || deal?.error);
    }

    openLinkInNewTab(shareLink);
    return '';
  };

  render() {
    const {
      vinData,
      vehicleData,
      isMobile,
      zipCode,
      pathName,
      isNonTMVDealer,
      showPriceAnalysisSection,
      radius,
      dealershipInfo,
      disableInventoryMLPhotoSort,
      forceLease,
      doNotSell,
      stateCode,
      isBLT2125DisableCarmax360,
      isBLT2125DisableSpinCar360,
      isSHOP413DisableFlickFusion360,
      inventory,
      isPrequalified,
      financingOffers,
      warranty,
      icoWidgetConfig,
      dealer,
      enableCaramel,
      ted6504Recipe,
      disableDrivewayNewCarLeadForms,
      location,
      privateListingData,
      styleFeatures,
      isAuthenticated,
      userId,
      isDealerFeesEnabled,
    } = this.props;
    const { calculatorPayments, openedCalculatorForm, isIncentivesAdRendered, isDRLeadFormSubmitAvailable } =
      this.state;
    const isCpoType = isCpo(vinData.type);
    const tmvPrice = get(vinData, 'prices.tmv', 0);
    const styleInfo = get(vinData, 'vehicleInfo.styleInfo', {});
    const { make, model, year, subModels } = styleInfo;
    const submodel = get(subModels, '[0].name', '');
    const submodelIdentifier = get(subModels, '[0].identifier', '');
    const { vin, type, stockNumber } = vinData;
    const isNewVehicle = isNew(type);
    const advertisedPrice = get(vinData, 'prices.advertisedPrice');
    const params = { make, model, year };
    const isPartnerListing = isPartnerDealer(vinData);
    const shouldShowDealerFeesPriceDetails =
      isDealerFeesEnabled && isPartnerListing && (hasDealerFees(vinData) || hasNoDealerFees(vinData));
    const purchasePrice = getPurchasePrice(vinData);
    const digitalRetailAvailability = this.getDigitalRetailAvailability(isNewVehicle);
    const { isDigitalRetailAvailable } = digitalRetailAvailability;
    const vdpClassName = classnames('vdp', { 'large-vdp': !isMobile });
    const daysToSell = get(vinData, 'computedDisplayInfo.estimatedDaysToSell');
    const parentDealershipName = get(vinData, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
    const isCarMaxDealer = isCarMax(parentDealershipName);
    const isDrivewayDealer = isDriveway(parentDealershipName);
    const isBolExtDealer = BOL_VDP_DEALERS.includes(parentDealershipName);
    const isUsedVehicle = !isNewVehicle;
    const partnerCode = vinData?.dealerInfo?.partnerCode;
    const tcpaParentDealershipName = getTCPAParentDealershipName({ parentDealershipName, partnerCode });
    const isStickyEnabled = !isMobile;
    const isUsedBasic = isUsedVehicle && !isPartnerListing;
    const ctas = getVdpCtas({ vinData, location });
    const isPurchase = openedCalculatorForm === CALCULATOR_TYPE_LOAN;
    const { isPriceDrop } = getPriceChange({ vinData });
    const isPartnerUsed = isUsedVehicle && isPartnerListing;
    const dealerStateCode = get(vinData, 'dealerInfo.address.stateCode', '');
    const concealHistoryPrices = dealerStateCode === HISTORY_PRICES_CONCEALED_STATE;
    const radiusValue = radius || 50;
    const showEvModule = isEvModuleApplicable(vinData);
    const showPhevModule = isPlugInHybridVehicle(vinData);
    const isPrivatePartyListing = isPrivateParty(vinData);

    const hideLeadFormForDrivewayDealer =
      disableDrivewayNewCarLeadForms && isNewVehicle && isDrivewayDealer && isPartnerListing;

    const hideLeadForm = isPrivatePartyListing || hideLeadFormForDrivewayDealer;

    const isTED6504Enabled = ['chal-1', 'chal-2'].includes(ted6504Recipe) && isCarMaxDealer;

    const isFinancingError = !isNull(financingOffers) && (get(financingOffers, 'isError') || isEmpty(financingOffers));
    const isEligibleVIN = isEligible(vinData);
    const isCapOneIneligible = !isEligibleVIN && isAvailableLocation(stateCode) && isUsedVehicle;
    const showCapOnePersonalizedPayment =
      isPrequalified && isCapOneProvider(vinData) && !isEmpty(financingOffers) && !isFinancingError && isEligibleVIN;
    const isCapOnePrequalificationAvailable = !isPrequalified && isAvailableLocation(stateCode);
    const isCapOneMatchedDealer =
      isCapOneProvider(vinData) && isEligibleVIN && isAvailableLocation(stateCode) && isUsedVehicle;
    const isLeaseView = openedCalculatorForm === CALCULATOR_TYPE_LEASE;
    const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId');

    const calculatorLeasePayment = get(calculatorPayments, `${CALCULATOR_TYPE_LEASE}.widgetMonthlyPayment`);
    const leasePayment = getLeaseMonthlyPayment(vinData, LEASE_PAYMENT_DATA_PATH, calculatorLeasePayment);
    const isLeasePaymentEnabled = Boolean(forceLease && leasePayment);
    const showLeasePayments =
      isLeasePaymentEnabled &&
      openedCalculatorForm === CALCULATOR_TYPE_LEASE &&
      !!styleId &&
      isLeasePaymentsEligible(vinData);
    const showSellerDescriptionModule = isPrivatePartyListing && !!privateListingData?.sellerComments;
    const showManageListingModule = isPrivatePartyListing && isAuthenticated && userId === privateListingData?.userId;
    const showSellerModule =
      isPrivatePartyListing &&
      hasSellerAddressInfo(vinData) &&
      privateListingData?.listingStatus === LISTING_STATUS_APPROVED;

    const embedLeadFormParams = {
      params: {
        make,
        model,
        year,
        zipCode,
        radius: radiusValue,
        vin,
        submodel,
        submodelIdentifier,
        tcpaParentDealershipName,
        isNewVin: isNewVehicle,
        dealerName: parentDealershipName,
        isPartnerListing,
        partnerCode,
        type,
        isLease: isLeaseEnabled(location),
      },
      pathname: pathName,
      ctas,
      daysToSell,
      hideContactDealerTip: isPartnerUsed && isPriceDrop && !concealHistoryPrices,
    };
    const makeSlug = get(vehicleData, 'make.slug', '');
    const isUsedNonPartnerDealCheckAvailable = isUsedNonPartnerDealerDealCheckAvailable(vinData);
    const defaultIncentivesAd = (
      <IncentivesAd
        mobile={isMobile}
        wrapperClass={classnames({ 'mb-2': isIncentivesAdRendered })}
        includeMrectAdCall
        slotRenderEndListener={this.hideEmptyAdContainer}
      />
    );
    const getIncentiveAd = wrapperClass => (
      <IncentivesAd
        mobile={isMobile}
        wrapperClass={classnames(wrapperClass, {
          'w-100': isMobile,
        })}
        slotRenderEndListener={this.hideEmptyAdContainer}
      />
    );

    const isCleanTitle = get(vinData, 'historyInfo.cleanTitle', true); // set to true by default if the field is missing

    const { maxGreatPrice, maxGoodPrice, maxFairPrice } = get(vinData, 'computedDisplayInfo.priceValidation', {});
    const downPayment = getDefaultLeaseDownPayment(vinData);
    const term = get(vinData, 'prices.estimateLeasePromise.term');
    let priceSectionTitle = isUsedBasic ? '' : 'Price';
    priceSectionTitle = isNewVehicle ? 'Price Details' : priceSectionTitle;
    const tmvAPIoptions = {
      styleId,
      dealerZip: get(vinData, 'dealerInfo.address.zip'),
      colorId: get(vinData, 'vehicleInfo.vehicleColors.exterior.id'),
      optionIds: get(vinData, 'vehicleInfo.partsInfo.nonColorOptionIds', []).join(','),
    };
    const isUsedVdpPriceCheckerEnabled =
      isUsedVehicle &&
      !isUsedBasic &&
      isCleanTitle &&
      (isUsedDealCheckAvailable(vinData, location) || isUsedNonPartnerDealCheckAvailable) &&
      maxGreatPrice &&
      maxGoodPrice &&
      maxFairPrice &&
      !!get(vinData, 'computedDisplayInfo.priceValidation.listPriceEstimate', 0);

    const showPriceSection = (isUsedVehicle && isPartnerListing) || isNewVehicle;

    const vdpLeadFormParams = {
      dealerInfo: dealershipInfo,
      tcpaParentDealershipName,
      isDRLeadFormSubmitAvailable,
      isDigitalRetailAvailable: isUsedVehicle ? isDigitalRetailAvailable : false,
      calculatorPayments,
      isPurchase,
    };

    const showBatteryRangeModule = isBatteryRangeModuleEnabled(vinData);
    const showVehicleConditionModule = isPrivatePartyListing && !!privateListingData?.modsId;

    const similarInventories = (isArray(inventory) ? inventory : get(inventory, 'results', [])).filter(
      vehicle => vehicle.vin !== vin
    );

    const dealerIdForIco = getDealerRooftopId(this.props);

    const priceSummaryProps = {
      vinData,
      isMobile,
      calculatorPayments,
      isLeaseView,
      radius,
      embedLeadFormParams,
      tcpaParentDealershipName,
      forceLease,
      isBasicViewEnabled: isUsedVehicle ? isUsedBasic : true,
      isVdpPriceCheckerEnabled: isUsedVdpPriceCheckerEnabled,
      isVdp: true,
      isPrequalified,
      isFinancingError,
      financingOffers,
      dealer,
      enableCaramel,
      hideLeadForm,
      hideLeadFormForDrivewayDealer,
      ted6504Recipe,
      privateListingData,
      handlePrivatePartyMakeOffer: this.handlePrivatePartyMakeOffer,
      isDealerFeesEnabled,
    };
    const sellersComments = transformInventoryComments(vinData?.sellersComments ?? '');

    const dealerModulePartner = (
      <VdpGroup groupId={NAVIGATION_IDS.DEALER} className="mb-md-0">
        <ClientSideRender>
          <VdpModule
            className="bg-cool-gray-90 mb-1_5"
            headerTag="h2"
            sectionTitle="Dealer Information"
            titleClassName="heading-3"
          >
            {!!sellersComments && (
              <VdpModule className="bg-white p-md-1_5 mb-1" indentationSize={1}>
                <InventoryComments
                  vinData={vinData}
                  sectionTitle="Dealer's Description of this Vehicle"
                  sectionClassName="inventory-comments"
                  line={false}
                />
                {isNewVehicle && advertisedPrice > 0 && (
                  <span className="pp-price d-none">Price Promise Price: {advertisedPrice}</span>
                )}
                {isNewVehicle && tmvPrice > 0 && <span className="d-none">TMV Price: {tmvPrice}</span>}
              </VdpModule>
            )}
            {hasDealerAddressInfo(vinData) && (
              <VdpModule className="bg-white p-md-1_5 mb-1" indentationSize={1}>
                <DealerInfo vinData={vinData} {...(isTED6504Enabled && { ted6504Recipe })} />
              </VdpModule>
            )}

            <VdpModule className="bg-white p-md-1_5" indentationSize={1}>
              <DealerReviews
                vinData={vinData}
                reviewsToShow={1}
                sectionTitle="Dealership Reviews by Customers"
                isMobile={isMobile}
              />
            </VdpModule>
          </VdpModule>
        </ClientSideRender>
      </VdpGroup>
    );
    const dealerModuleBasic = hasDealerAddressInfo(vinData) && (
      <VdpGroup groupId={NAVIGATION_IDS.DEALER} className="mb-md-0">
        <ClientSideRender>
          <VdpModule
            className="bg-cool-gray-90 mb-1_5"
            headerTag="h2"
            sectionTitle="Dealer Information"
            titleClassName="heading-3"
          >
            <VdpModule className="bg-white p-md-1_5" indentationSize={1}>
              <DealerInfo vinData={vinData} {...(isTED6504Enabled && { ted6504Recipe })} />
            </VdpModule>
          </VdpModule>
        </ClientSideRender>
      </VdpGroup>
    );

    const sellerModule = showSellerModule && (
      <VdpGroup groupId={NAVIGATION_IDS.SELLER} className="mb-md-0">
        <VdpModule
          className="bg-cool-gray-90 mb-1_5"
          headerTag="h2"
          sectionTitle="Seller Information"
          titleClassName="heading-3"
        >
          <VdpModule className="bg-white p-md-1_5 seller-info-module" indentationSize={1}>
            <SellerInfo
              enableCaramel={enableCaramel}
              vinData={vinData}
              handlePrivatePartyMakeOffer={this.handlePrivatePartyMakeOffer}
              dealer={dealer}
            />
          </VdpModule>
        </VdpModule>
      </VdpGroup>
    );

    const sellerDescriptionModule = (
      <VdpModule className="bg-white p-md-1_5 mt-md-1" indentationSize={1}>
        <SellerComments text={privateListingData?.sellerComments} isMobile={isMobile} />
      </VdpModule>
    );

    const consumerReviewModule = (
      <ErrorBoundary>
        {vehicleData && (
          <ClientSideRender>
            <VdpGroup groupId={NAVIGATION_IDS.REVIEWS}>
              <ConsumerReviewVdp
                vehicle={vehicleData}
                dataTrackingParent="edm-entry-consumer-reviews"
                isBasicViewEnabled={isUsedBasic}
                isPrivatePartyListing={isPrivatePartyListing}
              />
            </VdpGroup>
          </ClientSideRender>
        )}
        {isPartnerListing && <VdpAdUnit position={'2'} renderWhenViewable={false} />}
      </ErrorBoundary>
    );
    const phevModuleElement = (
      <ClientSideRender>
        <PhevInsights vinData={vinData} isMobile={isMobile} isPurchase={isPurchase} withCollapse={!isPartnerListing} />
      </ClientSideRender>
    );

    const isIcoWidgetActive = get(icoWidgetConfig, 'isActive', false);
    const icoModuleNonPartner = !isPartnerListing && isIcoWidgetActive && (
      <div
        data-tracking-parent={TrackingConstant.CREATIVE_ID_EDM_ENTRY_ICO_DEFAULT_EDMUNDS}
        className="ico-on-vdp-container"
      >
        {dealerIdForIco && (
          <IcoWidgetAsync
            dealerId={`${dealerIdForIco}`}
            displayType={ICO_WIDGET_DISPLAY_TYPES.DEFAULT_EDMUNDS_THEME}
            injectInitializationScriptOnMount
            applyToCalculator
          />
        )}
      </div>
    );
    const vehicleCondition = showVehicleConditionModule && (
      <ScrollElement id={NAVIGATION_IDS.CONDITION}>
        <VdpModule
          className="bg-cool-gray-90 mb-1_5"
          headerTag="h2"
          sectionTitle="Vehicle Condition"
          titleClassName="heading-3"
          subCopy="as reported by owner"
        >
          <VdpModule className="bg-white" indentationSize={1.5}>
            <VehicleCondition vinData={vinData} modsId={privateListingData.modsId} />
          </VdpModule>
        </VdpModule>
      </ScrollElement>
    );
    const isNewStyle =
      !!styleId && [PUB_STATES.NEW, PUB_STATES.NEW_USED].includes(vinData?.vehicleInfo?.styleInfo?.publicationState);

    const mainColUsed = (
      <Fragment>
        <ErrorBoundary>
          <Fragment>
            <Row>
              <Col xs={12} md={12}>
                <ScrollElement id={NAVIGATION_IDS.SUMMARY}>
                  <VdpModule
                    className="bg-cool-gray-90 mb-1_5"
                    headerTag="h2"
                    sectionTitle="Vehicle Summary"
                    titleClassName="heading-3"
                  >
                    <VdpModule className="bg-white" indentationSize={1.5}>
                      <VehicleSummary
                        vinData={vinData}
                        isMobile={isMobile}
                        vdpLeadFormParams={vdpLeadFormParams}
                        excludeBatteryInfo={isUsedVehicle}
                      />
                    </VdpModule>
                    {(isPartnerListing || !similarInventories.length) && (
                      <VehicleExtendedDetails vinData={vinData} vehicleData={vehicleData} />
                    )}
                  </VdpModule>
                </ScrollElement>
                {showEvModule && isPartnerListing && (
                  <ClientSideRender>
                    <ScrollElement id={NAVIGATION_IDS.EV_INSIGHTS}>
                      <EvInsights vinData={vinData} isMobile={isMobile} isPurchase={isPurchase} />
                    </ScrollElement>
                  </ClientSideRender>
                )}
                {showPhevModule && isPartnerListing && (
                  <ScrollElement id={NAVIGATION_IDS.PHEV_INSIGHTS}>{phevModuleElement}</ScrollElement>
                )}
                {!isPartnerListing &&
                  isCapOnePrequalificationAvailable &&
                  !isTED6504Enabled &&
                  !isPrivatePartyListing && (
                    <CapitalOneEntryPointMainWell
                      vinData={vinData}
                      isMobile={isMobile}
                      payments={calculatorPayments}
                      isCapOneIneligible={isCapOneIneligible}
                    />
                  )}
                {!isPartnerListing && !isPrivatePartyListing && (
                  <VdpAppraisalModule isMobile={isMobile} dealerId={dealerIdForIco} />
                )}
                {icoModuleNonPartner}
                {!isPartnerListing && (
                  <Fragment>
                    {!isPrivatePartyListing && (
                      <ClientSideRender>
                        <VdpSimilarVehicles
                          vinData={vinData}
                          isMobile={isMobile}
                          radius={radiusValue}
                          paymentType={isPurchase ? LOAN_PAYMENT : LEASE_PAYMENT}
                          isNewDesign={isUsedBasic}
                          injectEasListing
                          isUsedBasic={isUsedBasic}
                        />
                      </ClientSideRender>
                    )}
                    {showSellerDescriptionModule && (
                      <VdpModule className="bg-cool-gray-90 mb-1_5">{sellerDescriptionModule}</VdpModule>
                    )}
                    {!!similarInventories.length && (
                      <VehicleExtendedDetails
                        className="mb-1_5"
                        vinData={vinData}
                        vehicleData={vehicleData}
                        isSeparateModule
                      />
                    )}
                  </Fragment>
                )}
                {isMobile && showEvModule && !isPartnerListing && (
                  <ClientSideRender>
                    <ScrollElement id={NAVIGATION_IDS.EV_INSIGHTS} className="mb-1_5">
                      <EvInsights vinData={vinData} isMobile={isMobile} isPurchase={isPurchase} withCollapse />
                    </ScrollElement>
                  </ClientSideRender>
                )}
                {isMobile && showPhevModule && !isPartnerListing && (
                  <ScrollElement id={NAVIGATION_IDS.PHEV_INSIGHTS} className="mb-2_5">
                    {phevModuleElement}
                  </ScrollElement>
                )}
                {isMobile && vehicleCondition}
                <ScrollElement id={NAVIGATION_IDS.FEATURES}>
                  {isPrivatePartyListing ? (
                    <FeaturesAndSpecsPrivate
                      vinData={vinData}
                      styleFeatures={styleFeatures}
                      privateListingData={privateListingData}
                      className="mb-1_5"
                    />
                  ) : (
                    <FeaturesAndSpecs
                      vinData={vinData}
                      isMobile={isMobile}
                      collapseOnLoad={isUsedBasic}
                      className={classnames({ 'mb-1_5': !isUsedBasic })}
                    />
                  )}
                </ScrollElement>
                {isBolExtDealer && (
                  <Fragment>
                    {isTED6504Enabled ? (
                      <CapitalOneEntryPointMainWell
                        vinData={vinData}
                        isMobile={isMobile}
                        payments={calculatorPayments}
                        isCapOneIneligible={isCapOneIneligible}
                        ted6504Recipe={ted6504Recipe}
                      />
                    ) : (
                      <MidPageWidgetView
                        vinData={vinData}
                        zipCode={zipCode}
                        dealerInfo={dealershipInfo}
                        isMobile={isMobile}
                      />
                    )}
                  </Fragment>
                )}
              </Col>
            </Row>
            {isCpo(type) && (
              <ClientSideRender>
                <VdpCertifiedPreOwned
                  make={make}
                  className="mb-1_5"
                  contentCollapseClasses="mb-3"
                  isMobile={isMobile}
                />
              </ClientSideRender>
            )}
            {isPartnerListing && defaultIncentivesAd}
          </Fragment>
        </ErrorBoundary>
        {!isPrivatePartyListing && (
          <ErrorBoundary>
            <Fragment>
              <VdpGroup groupId={NAVIGATION_IDS.PRICE}>
                <VdpModule
                  className="bg-cool-gray-90 mb-1_5"
                  headerTag="h2"
                  sectionTitle={priceSectionTitle}
                  titleClassName="heading-3"
                >
                  {isUsedVdpPriceCheckerEnabled && (
                    <ClientSideRender>
                      <VdpModule className="bg-white p-md-1_5" indentationSize={1}>
                        <VdpPriceChecker
                          vin={vin}
                          stockNumber={stockNumber}
                          isMobile={isMobile}
                          isNewVin={false}
                          isLeasePaymentEnabled={isLeasePaymentEnabled}
                          radius={radiusValue}
                          hideLeadForm={hideLeadForm}
                          partnerCode={partnerCode}
                          make={make}
                          isPurchase={isPurchase}
                        />
                      </VdpModule>
                    </ClientSideRender>
                  )}
                  {shouldShowDealerFeesPriceDetails && (
                    <div className="mt-1">
                      <DealerFeesPriceDetails vinData={vinData} isLeaseView={isLeaseView} />
                    </div>
                  )}
                  {!shouldShowDealerFeesPriceDetails && !!advertisedPrice && (
                    <div className="mt-1">
                      <PriceDetails vinData={vinData} />
                    </div>
                  )}
                  {!isUsedBasic && (
                    <VdpModule className="bg-white mt-1 p-md-1_5" indentationSize={1}>
                      <Negotiation
                        vinData={vinData}
                        isMobile={isMobile}
                        isUsedNonPartnerDealCheckAvailable={isUsedNonPartnerDealCheckAvailable}
                      />
                    </VdpModule>
                  )}
                </VdpModule>
              </VdpGroup>

              <VdpGroup groupId={NAVIGATION_IDS.CALCULATOR}>
                {showCapOnePersonalizedPayment && isAvailableLocation(stateCode) ? (
                  <CapitalOnePersonalizedPayment vinData={vinData} financingOffers={financingOffers} />
                ) : (
                  <VdpPaymentCalculatorAsync
                    vinData={vinData}
                    openedForm={openedCalculatorForm}
                    payments={calculatorPayments}
                    onPaymentsUpdate={this.handleCalculatorPaymentsUpdate}
                    onCalculatorToggle={this.handleCalculatorToggle}
                    isNonTMVDealer={isNonTMVDealer}
                    price={purchasePrice}
                    onLeadFormSubmitMethodUpdate={this.handleLeadFormSubmitMethodUpdate}
                    radius={radius}
                    showCapOneEntryPoint={isCapOneMatchedDealer}
                  />
                )}
              </VdpGroup>

              <VdpAdUnit />
            </Fragment>
          </ErrorBoundary>
        )}
        {isWarrantyApplicable(vinData) && isWarrantyDefined(vinData, warranty) && !isPrivatePartyListing && (
          <VdpGroup groupId={NAVIGATION_IDS.WARRANTY}>
            <WarrantyModule warranty={warranty} vinData={vinData} className="mb-1_5" />
          </VdpGroup>
        )}
        {isPartnerListing ? (
          <>
            <ErrorBoundary>{dealerModulePartner}</ErrorBoundary>
            {!similarInventories.length && consumerReviewModule}
          </>
        ) : (
          <>
            <ErrorBoundary>{isPrivatePartyListing ? sellerModule : dealerModuleBasic}</ErrorBoundary>
            {consumerReviewModule}
          </>
        )}
      </Fragment>
    );
    const mainColNew = isPartnerListing ? (
      <Fragment>
        <ErrorBoundary>
          <Fragment>
            <Row>
              <Col xs={12} md={12}>
                <ScrollElement id={NAVIGATION_IDS.SUMMARY}>
                  <VdpModule
                    className="bg-cool-gray-90 mb-1_5"
                    headerTag="h2"
                    sectionTitle="Vehicle Summary"
                    titleClassName="heading-3"
                  >
                    <VdpModule className="bg-white" indentationSize={1.5}>
                      <VehicleSummary
                        vinData={vinData}
                        isMobile={isMobile}
                        vdpLeadFormParams={vdpLeadFormParams}
                        excludeBatteryInfo={isUsedVehicle}
                      />
                    </VdpModule>
                    <VehicleExtendedDetails vinData={vinData} vehicleData={vehicleData} />
                  </VdpModule>
                </ScrollElement>
                {showEvModule && (
                  <ClientSideRender>
                    <ScrollElement id={NAVIGATION_IDS.EV_INSIGHTS}>
                      <EvInsights vinData={vinData} isMobile={isMobile} isPurchase={isPurchase} />
                    </ScrollElement>
                  </ClientSideRender>
                )}
                {showPhevModule && <ScrollElement id={NAVIGATION_IDS.PHEV_INSIGHTS}>{phevModuleElement}</ScrollElement>}
                <ScrollElement id={NAVIGATION_IDS.FEATURES}>
                  <FeaturesAndSpecs vinData={vinData} isMobile={isMobile} className="mb-1_5" />
                </ScrollElement>
                {isDrivewayDealer && (
                  <MidPageWidgetView
                    vinData={vinData}
                    zipCode={zipCode}
                    dealerInfo={dealershipInfo}
                    isMobile={isMobile}
                  />
                )}
              </Col>
            </Row>
            {defaultIncentivesAd}
          </Fragment>
        </ErrorBoundary>
        <ErrorBoundary>
          <Fragment>
            <VdpGroup groupId={NAVIGATION_IDS.PRICE}>
              <VdpModule
                className="bg-cool-gray-90 mb-1_5"
                headerTag="h2"
                sectionTitle={priceSectionTitle}
                titleClassName="heading-3"
              >
                {(isNewStyle || !!maxGoodPrice) && (
                  <ClientSideRender>
                    <VdpModule className="bg-white p-md-1_5" indentationSize={1}>
                      <VdpPriceChecker
                        vin={vin}
                        stockNumber={stockNumber}
                        isMobile={isMobile}
                        showLeasePayments={showLeasePayments}
                        downPayment={downPayment}
                        term={term}
                        isNewVin
                        isLeasePaymentEnabled={isLeasePaymentEnabled}
                        make={make}
                        model={model}
                        year={year}
                        tmvAPIoptions={tmvAPIoptions}
                        radius={radiusValue}
                        showBottomLine={false}
                        sectionClasses="new-price-checker"
                        isLeaseView={isLeaseView}
                        forceLease={forceLease}
                        hideLeadForm={hideLeadForm}
                        isPurchase={isPurchase}
                      />
                    </VdpModule>
                  </ClientSideRender>
                )}
                {shouldShowDealerFeesPriceDetails && (
                  <div className="mt-1">
                    <DealerFeesPriceDetails vinData={vinData} isLeaseView={isLeaseView} />
                  </div>
                )}
                {!shouldShowDealerFeesPriceDetails && !!advertisedPrice && (
                  <div className="mt-1">
                    <PriceDetails vinData={vinData} />
                  </div>
                )}
                <VdpModule className="bg-white mt-1 p-md-1_5" indentationSize={1}>
                  <Negotiation vinData={vinData} isMobile={isMobile} />
                </VdpModule>
              </VdpModule>
            </VdpGroup>
            <VdpGroup groupId={NAVIGATION_IDS.CALCULATOR}>
              <VdpPaymentCalculatorAsync
                vinData={vinData}
                openedForm={openedCalculatorForm}
                payments={calculatorPayments}
                onPaymentsUpdate={this.handleCalculatorPaymentsUpdate}
                onCalculatorToggle={this.handleCalculatorToggle}
                isNonTMVDealer={isNonTMVDealer}
                price={purchasePrice}
                onLeadFormSubmitMethodUpdate={this.handleLeadFormSubmitMethodUpdate}
                radius={radius}
              />
            </VdpGroup>
            {isWarrantyApplicable(vinData) && isWarrantyDefined(vinData, warranty) && (
              <VdpGroup groupId={NAVIGATION_IDS.WARRANTY}>
                <WarrantyModule warranty={warranty} vinData={vinData} className="mb-1_5" />
              </VdpGroup>
            )}
            <VdpAdUnit />
          </Fragment>
        </ErrorBoundary>
        <ErrorBoundary>{dealerModulePartner}</ErrorBoundary>
        {!similarInventories.length && consumerReviewModule}
      </Fragment>
    ) : (
      <Fragment>
        <ErrorBoundary>
          <Fragment>
            {(isNewStyle || !!maxGoodPrice) && (
              <ClientSideRender>
                <VdpGroup groupId={NAVIGATION_IDS.PRICE} groupTitle={showPriceAnalysisSection ? 'Price Analysis' : ''}>
                  <VdpPriceChecker
                    vin={vin}
                    stockNumber={stockNumber}
                    isMobile={isMobile}
                    showLeasePayments={showLeasePayments}
                    downPayment={downPayment}
                    term={term}
                    isNewVin
                    isLeasePaymentEnabled={isLeasePaymentEnabled}
                    make={make}
                    model={model}
                    year={year}
                    tmvAPIoptions={tmvAPIoptions}
                    radius={radiusValue}
                    isLeaseView={isLeaseView}
                    forceLease={forceLease}
                    isPurchase={isPurchase}
                  />
                </VdpGroup>
              </ClientSideRender>
            )}
            <VdpGroup groupId={NAVIGATION_IDS.CALCULATOR}>
              <VdpPaymentCalculatorAsync
                vinData={vinData}
                openedForm={openedCalculatorForm}
                payments={calculatorPayments}
                onPaymentsUpdate={this.handleCalculatorPaymentsUpdate}
                onCalculatorToggle={this.handleCalculatorToggle}
                isNonTMVDealer={isNonTMVDealer}
                price={purchasePrice}
                onLeadFormSubmitMethodUpdate={this.handleLeadFormSubmitMethodUpdate}
                radius={radius}
              />
            </VdpGroup>
            {!isPartnerListing && (
              <ClientSideRender>
                <VdpSimilarVehicles vinData={vinData} isMobile={isMobile} injectEasListing isUsedBasic={isUsedBasic} />
              </ClientSideRender>
            )}
            <IncentivesAd
              mobile={isMobile}
              wrapperClass={classnames({ 'mb-2': isIncentivesAdRendered })}
              includeMrectAdCall
              slotRenderEndListener={this.hideEmptyAdContainer}
            />

            <Negotiation className="mb-1_5" vinData={vinData} isMobile={isMobile} />
          </Fragment>
        </ErrorBoundary>
        <ErrorBoundary>
          <ScrollElement id={NAVIGATION_IDS.DETAILS}>
            <Row>
              <Col xs={12} md={12}>
                <ScrollElement id={NAVIGATION_IDS.SUMMARY}>
                  <VdpModule
                    className="bg-cool-gray-90 mb-1_5"
                    headerTag="h2"
                    sectionTitle="Vehicle Summary"
                    titleClassName="heading-3"
                  >
                    <VdpModule className="bg-white" indentationSize={1.5}>
                      <VehicleSummary
                        vinData={vinData}
                        isMobile={isMobile}
                        vdpLeadFormParams={vdpLeadFormParams}
                        excludeBatteryInfo={isUsedVehicle}
                      />
                    </VdpModule>
                    <VehicleExtendedDetails vinData={vinData} vehicleData={vehicleData} />
                  </VdpModule>
                </ScrollElement>
                {!isPartnerListing && <VdpAppraisalModule isMobile={isMobile} dealerId={dealerIdForIco} />}
                {icoModuleNonPartner}
                <ScrollElement id={NAVIGATION_IDS.FEATURES}>
                  <FeaturesAndSpecs className="mb-1_5" vinData={vinData} isMobile={isMobile} />
                </ScrollElement>
              </Col>
            </Row>
            <ClientSideRender>
              <InventoryComments vinData={vinData} />
            </ClientSideRender>
            {advertisedPrice > 0 && <span className="pp-price d-none">Price Promise Price: {advertisedPrice}</span>}
            {tmvPrice > 0 && <span className="d-none">TMV Price: {tmvPrice}</span>}
            {vehicleData && (
              <Fragment>
                <VdpGroup groupId={NAVIGATION_IDS.REVIEWS}>
                  <ClientSideRender>
                    <ConsumerReviewVdp vehicle={vehicleData} dataTrackingParent="edm-entry-consumer-reviews" />
                  </ClientSideRender>
                  <VdpAdUnit />
                </VdpGroup>
              </Fragment>
            )}
          </ScrollElement>
        </ErrorBoundary>
        <ErrorBoundary>{dealerModuleBasic}</ErrorBoundary>
      </Fragment>
    );
    const mainCol = isUsedVehicle ? mainColUsed : mainColNew;

    const inventoryHeroProps = {
      vinData,
      isCpo: isCpoType,
      makeSlug,
      isMobile,
      photo360: getPhoto360({
        vinData,
        doNotSell,
        isBLT2125DisableCarmax360,
        isBLT2125DisableSpinCar360,
        isSHOP413DisableFlickFusion360,
      }),
      disableInventoryMLPhotoSort,
      showMultipleImages: true,
      hideLeadForm,
    };
    const heroImg = (
      <ErrorBoundary>
        <InventoryHero
          {...inventoryHeroProps}
          {...(((isUsedBasic && !isDrivewayDealer) || (isPartnerListing && isDrivewayDealer && isNewVehicle)) &&
          !isPrivatePartyListing
            ? { showCapOneEntryPoint: true }
            : {})}
        />
      </ErrorBoundary>
    );
    const mainColBasicFirstPart = (
      <Fragment>
        <ErrorBoundary>
          <ScrollElement id={NAVIGATION_IDS.DETAILS}>
            <Row>
              <Col xs={12} md={12}>
                <ScrollElement id={NAVIGATION_IDS.SUMMARY}>
                  <VdpModule
                    className="bg-cool-gray-90 mb-1_5"
                    headerTag="h2"
                    sectionTitle="Vehicle Summary"
                    titleClassName="heading-3"
                  >
                    <VdpModule className="bg-white" indentationSize={1.5}>
                      <VehicleSummary
                        vinData={vinData}
                        isMobile={isMobile}
                        vdpLeadFormParams={vdpLeadFormParams}
                        excludeBatteryInfo={isUsedVehicle}
                      />
                    </VdpModule>
                    {showSellerDescriptionModule && sellerDescriptionModule}
                    {isPrivatePartyListing && (
                      <VehicleExtendedDetails className="mb-1_5" vinData={vinData} vehicleData={vehicleData} />
                    )}
                  </VdpModule>
                </ScrollElement>
                {isPrivatePartyListing && vehicleCondition}
                {isCapOnePrequalificationAvailable && !isTED6504Enabled && !isPrivatePartyListing && (
                  <CapitalOneEntryPointMainWell
                    vinData={vinData}
                    isMobile={isMobile}
                    payments={calculatorPayments}
                    isCapOneIneligible={isCapOneIneligible}
                  />
                )}
                {!isPartnerListing && !isPrivatePartyListing && (
                  <VdpAppraisalModule isMobile={isMobile} dealerId={dealerIdForIco} />
                )}
                {icoModuleNonPartner}
              </Col>
            </Row>
          </ScrollElement>
        </ErrorBoundary>
        {!similarInventories.length && consumerReviewModule}
      </Fragment>
    );
    const mainColBasicSecondPart = (
      <Fragment>
        <ErrorBoundary>
          <Row>
            <Col xs={12} md={12}>
              {!isPrivatePartyListing && (!isNewVehicle || showBatteryRangeModule) && (
                <VehicleExtendedDetails
                  className="mb-1_5"
                  vinData={vinData}
                  vehicleData={vehicleData}
                  isSeparateModule
                />
              )}
              {showEvModule && (
                <ClientSideRender>
                  <ScrollElement id={NAVIGATION_IDS.EV_INSIGHTS} className="mb-1_5">
                    <EvInsights vinData={vinData} isMobile={isMobile} isPurchase={isPurchase} withCollapse />
                  </ScrollElement>
                </ClientSideRender>
              )}
              {showPhevModule && (
                <ScrollElement id={NAVIGATION_IDS.PHEV_INSIGHTS} className="mb-2_5">
                  {phevModuleElement}
                </ScrollElement>
              )}
              <ScrollElement id={NAVIGATION_IDS.FEATURES}>
                {isPrivatePartyListing ? (
                  <FeaturesAndSpecsPrivate
                    vinData={vinData}
                    styleFeatures={styleFeatures}
                    privateListingData={privateListingData}
                    isMobile={isMobile}
                    className="mb-1_5"
                  />
                ) : (
                  <FeaturesAndSpecs
                    className={classnames({ 'mb-1_5': !isUsedBasic })}
                    vinData={vinData}
                    isMobile={isMobile}
                    collapseOnLoad={isUsedBasic}
                  />
                )}
              </ScrollElement>
              {isUsedVehicle && isBolExtDealer && (
                <Fragment>
                  {isTED6504Enabled ? (
                    <CapitalOneEntryPointMainWell
                      vinData={vinData}
                      isMobile={isMobile}
                      payments={calculatorPayments}
                      isCapOneIneligible={isCapOneIneligible}
                      ted6504Recipe={ted6504Recipe}
                    />
                  ) : (
                    <MidPageWidgetView
                      vinData={vinData}
                      zipCode={zipCode}
                      dealerInfo={dealershipInfo}
                      isMobile={isMobile}
                    />
                  )}
                </Fragment>
              )}
            </Col>
          </Row>
          {isCpo(type) && (
            <ClientSideRender>
              <VdpCertifiedPreOwned make={make} className="mb-1_5" contentCollapseClasses="mb-3" isMobile={isMobile} />
            </ClientSideRender>
          )}
        </ErrorBoundary>
        <ErrorBoundary>
          {!isPrivatePartyListing && (
            <VdpGroup groupId={NAVIGATION_IDS.CALCULATOR}>
              <Fragment>
                {isUsedVehicle && showCapOnePersonalizedPayment ? (
                  <CapitalOnePersonalizedPayment vinData={vinData} financingOffers={financingOffers} />
                ) : (
                  <VdpPaymentCalculatorAsync
                    vinData={vinData}
                    openedForm={openedCalculatorForm}
                    payments={calculatorPayments}
                    onPaymentsUpdate={this.handleCalculatorPaymentsUpdate}
                    onCalculatorToggle={this.handleCalculatorToggle}
                    isNonTMVDealer={isNonTMVDealer}
                    price={purchasePrice}
                    onLeadFormSubmitMethodUpdate={this.handleLeadFormSubmitMethodUpdate}
                    radius={radius}
                  />
                )}
                <VdpAdUnit />
              </Fragment>
            </VdpGroup>
          )}
        </ErrorBoundary>
        <ErrorBoundary>
          {isWarrantyApplicable(vinData) && isWarrantyDefined(vinData, warranty) && !isPrivatePartyListing && (
            <VdpGroup groupId={NAVIGATION_IDS.WARRANTY}>
              <WarrantyModule warranty={warranty} vinData={vinData} className="mb-1_5" />
            </VdpGroup>
          )}
        </ErrorBoundary>
        <ErrorBoundary>{isPrivatePartyListing ? sellerModule : dealerModuleBasic}</ErrorBoundary>
        {!!similarInventories.length && consumerReviewModule}
      </Fragment>
    );

    return (
      <main id="main-content" tabIndex={-1} className={classnames('pos-r', vdpClassName)} data-vdp-navigation-boundary>
        <ClippedContainer
          containerClasses={classnames('vdp-top-eve-header pos-a top-0 left-0 w-100', {
            mobile: isMobile,
          })}
          mask={convexMask}
        />
        <div className="pb-md-3_25" {...(isPartnerListing ? {} : { 'data-vdp-boundary': true })}>
          {isUsedBasic && !isMobile ? (
            <Fragment>
              <Container tag="div" className="vdp-content-wrapper">
                <Row>
                  <Col
                    xs={GRID.TWO_COL.XS.FULL_WIDTH}
                    md={GRID.TWO_COL.MD.LEFT_COLUMN}
                    lg={GRID.TWO_COL.LG.LEFT_COLUMN_LARGE}
                    className="mt-md-1 pe-xl-2"
                    data-vdp-boundary-basic
                  >
                    {heroImg}
                    {showManageListingModule && (
                      <ErrorBoundary>
                        <ManageListingContainerAsync listingData={privateListingData} isMobile={isMobile} />
                      </ErrorBoundary>
                    )}
                    <ErrorBoundary>
                      <VdpNavigation
                        vinData={vinData}
                        isMobile={isMobile}
                        disableSticky={isMobile}
                        showPriceSection={showPriceSection && !isPrivatePartyListing}
                        isCalculatorEnabled={!!purchasePrice && !isPrivatePartyListing}
                        vehicle={vehicleData}
                        params={params}
                        showEVModule={showEvModule && !isPrivatePartyListing}
                        showPhevModule={showPhevModule && !isPrivatePartyListing}
                        showSellerModule={showSellerModule}
                        showVehicleConditionModule={showVehicleConditionModule}
                      />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <ScrollElement id={NAVIGATION_IDS.OVERVIEW}>
                        <VinOverview vinData={vinData} isCpo={isCpoType} isMobile={isMobile} isVdp />
                      </ScrollElement>
                    </ErrorBoundary>
                    {mainColBasicFirstPart}
                    {(isPrivatePartyListing || !similarInventories.length) && mainColBasicSecondPart}
                  </Col>
                  <Col
                    xs={GRID.TWO_COL.XS.FULL_WIDTH}
                    md={GRID.TWO_COL.MD.RIGHT_COLUMN}
                    lg={GRID.TWO_COL.LG.RIGHT_COLUMN_LARGE}
                    xl={GRID.TWO_COL.XL.RIGHT_COLUMN}
                    className="px-0_25 px-md-0"
                  >
                    <ErrorBoundary>
                      <StickyToBottom offsetSelector=".vdp-navigation-container" enabled={isStickyEnabled}>
                        {() => (
                          <>
                            <div className={classnames('pos-r mt-1', { 'is-capOne-enabled': !isMobile })}>
                              <PriceSummary {...priceSummaryProps} />
                            </div>
                            {getIncentiveAd('pb-1_5')}
                          </>
                        )}
                      </StickyToBottom>
                    </ErrorBoundary>
                  </Col>
                </Row>
              </Container>
              {!isPrivatePartyListing && !!similarInventories.length && (
                <Fragment>
                  <ClientSideRender>
                    <VdpSimilarVehicles
                      vinData={vinData}
                      isMobile={isMobile}
                      radius={radiusValue}
                      paymentType={isPurchase ? LOAN_PAYMENT : LEASE_PAYMENT}
                      isNewDesign
                      injectEasListing
                      isUsedBasic={isUsedBasic}
                    />
                  </ClientSideRender>
                  <Container tag="div" className="vdp-content-wrapper">
                    <Row>
                      <Col
                        xs={GRID.TWO_COL.XS.FULL_WIDTH}
                        md={GRID.TWO_COL.MD.LEFT_COLUMN}
                        lg={GRID.TWO_COL.LG.LEFT_COLUMN_LARGE}
                        className="pe-xl-2"
                      >
                        {mainColBasicSecondPart}
                      </Col>
                    </Row>
                  </Container>
                </Fragment>
              )}
            </Fragment>
          ) : (
            <Fragment>
              <Container tag="div" className="vdp-content-wrapper">
                <Row>
                  <Col
                    xs={GRID.TWO_COL.XS.FULL_WIDTH}
                    md={GRID.TWO_COL.MD.LEFT_COLUMN}
                    lg={isMobile ? GRID.TWO_COL.LG.LEFT_COLUMN : GRID.TWO_COL.LG.LEFT_COLUMN_LARGE}
                    className="mt-md-1 pe-xl-2"
                    {...(isPartnerListing ? { 'data-vdp-boundary': true } : {})}
                  >
                    {heroImg}
                    {showManageListingModule && isMobile && (
                      <ErrorBoundary>
                        <ManageListingContainerAsync listingData={privateListingData} isMobile={isMobile} />
                      </ErrorBoundary>
                    )}
                    <ErrorBoundary>
                      <VdpNavigation
                        vinData={vinData}
                        isMobile={isMobile}
                        disableSticky={false}
                        showPriceSection={showPriceSection && !isPrivatePartyListing}
                        isCalculatorEnabled={!!purchasePrice && !isPrivatePartyListing}
                        vehicle={vehicleData}
                        params={params}
                        showEVModule={showEvModule && !isPrivatePartyListing}
                        showPhevModule={showPhevModule && !isPrivatePartyListing}
                        showSellerModule={showSellerModule}
                        showVehicleConditionModule={showVehicleConditionModule}
                      />
                    </ErrorBoundary>
                    {!isMobile && (
                      <ErrorBoundary>
                        <ScrollElement id={NAVIGATION_IDS.OVERVIEW}>
                          <VinOverview
                            vinData={vinData}
                            isCpo={isCpoType}
                            isMobile={isMobile}
                            showQRCode={isPartnerListing}
                            isVdp
                          />
                        </ScrollElement>
                      </ErrorBoundary>
                    )}
                    {!isMobile ? mainCol : null}
                  </Col>
                  {/* Setting id is needed for sticky mobile navigation */}
                  <Col
                    xs={GRID.TWO_COL.XS.FULL_WIDTH}
                    md={GRID.TWO_COL.MD.RIGHT_COLUMN}
                    lg={isMobile ? GRID.TWO_COL.LG.RIGHT_COLUMN : GRID.TWO_COL.LG.RIGHT_COLUMN_LARGE}
                    xl={GRID.TWO_COL.XL.RIGHT_COLUMN}
                    className="px-0_25 px-md-0"
                    {...(isMobile ? { id: NAVIGATION_IDS.OVERVIEW } : {})}
                  >
                    {isMobile && (
                      <ErrorBoundary>
                        <VinOverview vinData={vinData} isCpo={isCpoType} isMobile={isMobile} isVdp withSaveBtn />
                      </ErrorBoundary>
                    )}
                    <ErrorBoundary>
                      <StickyToBottom offsetSelector=".vdp-navigation-container" enabled={isStickyEnabled}>
                        {() => (
                          <>
                            <div className={classnames('pos-r mt-1', { 'is-capOne-enabled': !isMobile })}>
                              <PriceSummary {...priceSummaryProps} />
                            </div>
                            {!isMobile && !isPartnerListing && getIncentiveAd('pb-1_5')}
                          </>
                        )}
                      </StickyToBottom>
                    </ErrorBoundary>
                  </Col>
                  {isMobile && !isPartnerListing && getIncentiveAd('mb-1_5 mx-0_5')}
                  {isMobile ? (
                    <Col
                      xs={GRID.TWO_COL.XS.FULL_WIDTH}
                      md={GRID.TWO_COL.MD.LEFT_COLUMN}
                      lg={GRID.TWO_COL.LG.LEFT_COLUMN}
                      className="main-content-col"
                    >
                      {mainCol}
                    </Col>
                  ) : null}
                </Row>
              </Container>
              {isPartnerListing && !!similarInventories.length && (
                <Fragment>
                  <div className="bg-blue-90 overlay-white-40 pt-2 pb-0_5 mb-2 mt-2">
                    <Container tag="div" className="vdp-content-wrapper">
                      <Row>
                        <Col xs={GRID.TWO_COL.XS.FULL_WIDTH}>
                          <ErrorBoundary>
                            <ClientSideRender>
                              <VdpSimilarVehicles
                                vinData={vinData}
                                isMobile={isMobile}
                                radius={radiusValue}
                                paymentType={isPurchase ? LOAN_PAYMENT : LEASE_PAYMENT}
                                forceLease={forceLease}
                                isNewDesign
                                isPartnerListing
                              />
                            </ClientSideRender>
                          </ErrorBoundary>
                        </Col>
                      </Row>
                    </Container>
                  </div>
                  <Container tag="div" className="vdp-content-wrapper">
                    <Row>
                      <Col
                        xs={GRID.TWO_COL.XS.FULL_WIDTH}
                        md={GRID.TWO_COL.MD.LEFT_COLUMN}
                        lg={isMobile ? GRID.TWO_COL.LG.LEFT_COLUMN : GRID.TWO_COL.LG.LEFT_COLUMN_LARGE}
                        className={classnames({ 'pe-xl-2': !isMobile })}
                      >
                        {consumerReviewModule}
                      </Col>
                    </Row>
                  </Container>
                </Fragment>
              )}
            </Fragment>
          )}
        </div>
        {!isPartnerListing && !isPrivatePartyListing && (
          <Container>
            {!isUsedBasic && <hr />}
            <Experiment name="ads-12460_dfp" showDefault>
              <Recipe name="ctrl" isDefault>
                <SponsoredCars />
              </Recipe>
              <Recipe name="chal">
                <SponsoredCars showStackedAds inlineStyle={isMobile ? null : { maxWidth: '744px' }} />
              </Recipe>
            </Experiment>
            <Row>
              <Col
                xs={GRID.TWO_COL.XS.FULL_WIDTH}
                md={GRID.TWO_COL.MD.LEFT_COLUMN}
                lg={isMobile ? GRID.TWO_COL.LG.LEFT_COLUMN : GRID.TWO_COL.LG.LEFT_COLUMN_LARGE}
              >
                <VdpAdUnit position={'2'} renderWhenViewable={false} />
              </Col>
            </Row>
          </Container>
        )}
        <div className="mt-1 mt-md-1_75">
          <Container>
            <Row>
              <Col
                xs={GRID.TWO_COL.XS.FULL_WIDTH}
                lg={isMobile ? GRID.TWO_COL.LG.FULL_WIDTH : GRID.TWO_COL.LG.FULL_WIDTH_NO_OFFSET}
              >
                <ErrorBoundary>
                  <VdpDisclaimer vinData={vinData} withPublishDate={isNewVehicle} isVDP />
                </ErrorBoundary>
              </Col>
            </Row>
          </Container>
        </div>
        <div className="my-1 my-md-2">
          <Container>
            <Row>
              <Col
                xs={GRID.TWO_COL.XS.FULL_WIDTH}
                lg={isMobile ? GRID.TWO_COL.LG.FULL_WIDTH : GRID.TWO_COL.LG.FULL_WIDTH_NO_OFFSET}
              >
                <ErrorBoundary>
                  <VdpBreadcrumbs vinData={vinData} />
                </ErrorBoundary>
              </Col>
            </Row>
          </Container>
        </div>
        {isUsedVehicle && isCarMaxDealer && (
          <Experiment name="TED-6504-CAF-VDP-Prequal-Clickout" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal-0" />
            <Recipe name="chal-1" />
            <Recipe name="chal-2" />
          </Experiment>
        )}
        {isUsedVehicle && (
          <Experiment name="shop-5148-uci-copy" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal" />
          </Experiment>
        )}
        {isNewVehicle && (
          <Experiment name="shop-5146-nci-copy" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal" />
          </Experiment>
        )}
        {isPartnerUsed && (
          <Experiment name="shop-4713-prequal2" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal" />
          </Experiment>
        )}
      </main>
    );
  }
}

export const mapStateToProps = state => ({
  disableInventoryMLPhotoSort: !!get(state, 'featureFlags["disable-inventory-ml-photo-sort"]'),
  pageNameLegacy: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
  ted6504Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'TED-6504-CAF-VDP-Prequal-Clickout',
    defaultVal: 'ctrl',
  }),
  disableDrivewayNewCarLeadForms: !!get(state, 'featureFlags["disableDrivewayNewCarLeadForms"]'),
  location: get(state, 'pageContext.location'),
  visitorId: state.visitor?.id,
  sessionId: state.visitor?.session,
  isAuthenticated: state.profile?.isAuthenticated,
  userId: state.profile?.data?.idm?.identifiers?.firebase,
  isDealerFeesEnabled: !!state.featureFlags['shop-4751-dealer-fees'],
});

export const VdpInnerWrapper = connect(mapStateToProps)(
  connectToModel(VdpUI, {
    dealershipInfo: bindToPath(
      ({ vinData }) => (!isPrivateParty(vinData) ? getDealerInfoPath(vinData) : null),
      DealerInfoModel
    ),
    inventory: bindToPath('vinsV2', InventorySimilarVinsModel),
    financingOffers: bindToPath(
      ({ vinData, isPrequalified, isAllowBypassApiGateway }) =>
        isPrequalified && isCapOneProvider(vinData)
          ? PATHS.FINANCING_OFFERS({ vin: get(vinData, 'vin'), isAllowBypassApiGateway })
          : null,
      FinancingModel
    ),
    warranty: bindToPath(({ vinData }) => getVinWarrantyPath(vinData), InventoryModel),
    icoWidgetConfig: bindToPath(
      props =>
        buildDealerIcoWidgetConfigurationPath({
          rooftopId: getDealerRooftopId(props),
        }),
      DealerIcoWidgetModel
    ),
    dealer: bindToPath(({ enableCaramel, vinData }) => {
      const isCaramelEligible = enableCaramel && (isUsed(get(vinData, 'type', '')) || isPrivateParty(vinData));

      if (isCaramelEligible) {
        return DigitalCheckoutPaths.DEALERS_WITHOUT_VALIDATION({ vin: get(vinData, 'vin', '') });
      }
      return null;
    }, DigitalCheckoutModel),
    privateListingData: bindToPath(
      ({ vinData }) =>
        isPrivateParty(vinData) && vinData?.stockNumber
          ? PrivateListingPaths.LISTING_DATA({ listingId: vinData.stockNumber })
          : null,
      PrivateListingModel
    ),
    styleFeatures: bindToPath(
      ({ vinData }) =>
        isPrivateParty(vinData) &&
        vinData?.vehicleInfo?.styleInfo?.styleId &&
        FeatureSpecsPaths.buildStyleFeaturesPath(vinData.vehicleInfo.styleInfo.styleId),
      VehicleModel
    ),
    protocol: bindToPath('protocol', RequestModel),
    hostName: bindToPath('hostName', RequestModel),
  })
);

export const VdpWrapper = withInventoryListTracking(VdpInnerWrapper);
